import React, {useMemo} from 'react';
import {createColumnHelper} from '@tanstack/react-table';
import _ from 'lodash';
import {NameRenderer, FitRendererWithPopover} from './AccountGrid.components';
import {ListsRenderer} from './ListsRenderer';
import {IntentTierRendererWithPopover} from './IntentTierRenderer';
import {Checkmark} from '@carbon/icons-react';
import {
  Box,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  SimpleGrid,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {ScoredAccount} from '../../shared/scoredAccounts';
import {CountryList} from '../../shared/countries';
import {CategoriesAndAudienceRenderer} from './CategoriesAndAudienceRenderer';
import {useAccountView} from './AccountViewContext';
import {
  useIsFeatureEnabled,
  useIsFreemiumV3Customer,
} from '../../hooks/api/metadata';
import {FitSignalsWithPopover} from './FitSignalsWithPopover';
import {IntentSignalsWithPopover} from './IntentSignalsWithPopover';
import {AccountView} from '../../shared/filter/accountFilter';

const columnHelper = createColumnHelper<ScoredAccount>();

const selectColumn = columnHelper.display({
  id: 'select',
  // We render the markup ourselves in the table view (since we need non-table state)
  header: () => null,
  cell: () => null,
  meta: {
    width: 40,
    doesntOpenDetails: true,
  },
});

const accountColumn = (accountView: AccountView) => {
  return columnHelper.accessor(
    (acc): [string, string, string, boolean] => [
      acc.fields.name || acc.accountDomain,
      acc.accountDomain,
      acc.accountLinkedIn,
      acc.inCrm,
    ],
    {
      id: 'account',
      cell: (info) => (
        <NameRenderer
          name={info.getValue()[0]}
          domain={info.getValue()[1]}
          liUrl={info.getValue()[2]}
          inCrm={info.getValue()[3]}
          showCrmStatus={accountView !== 'recommended'}
        />
      ),
      header: 'Account',
      meta: {
        width: 200,
      },
    }
  );
};

const tierColumn = columnHelper.accessor(
  (acc) => [
    acc.tier,
    acc.mutatedState.tierOverride,
    acc.mutatedState.displayTier,
  ],
  {
    id: 'Tier',
    cell: (info) => {
      const [originalTier, tierOverride, tierToDisplay] = info.getValue();
      if (tierOverride !== 'none') {
        // TODO: tooltips are just going to further slow scrolling down.
        // I might need to create a custom implementation here at some point
        return (
          <Tooltip label={`Overridden from tier ${originalTier}`}>
            <span>{tierToDisplay}*</span>
          </Tooltip>
        );
      }
      return tierToDisplay;
    },
    header: () => {
      const tierTooltip = (
        <SimpleGrid columns={2}>
          <Box fontWeight="bold" textAlign="center">
            Tier
          </Box>
          <Box fontWeight="bold" textAlign="center">
            Overall Fit
          </Box>
          <Box textAlign="center">A</Box>
          <Box textAlign="center">70+</Box>
          <Box textAlign="center">B</Box>
          <Box textAlign="center">50+</Box>
          <Box textAlign="center">C</Box>
          <Box textAlign="center">30+</Box>
          <Box textAlign="center">D</Box>
          <Box textAlign="center">&lt;30</Box>
        </SimpleGrid>
      );
      return (
        <Tooltip hasArrow label={tierTooltip} placement="top">
          Tier
        </Tooltip>
      );
    },
    meta: {
      width: 45,
    },
  }
);

const icpFitColumn = columnHelper.accessor((acc) => acc.icpFit, {
  id: 'icpFit',
  cell: (info) => info.getValue(),
  header: 'ICP Fit',
  meta: {
    width: 70,
  },
});

const overallFitColumn = columnHelper.accessor((acc) => acc.marketScores[0], {
  id: 'overallFit',
  cell: (info) => {
    const {overallFit, signalScore, mostSimilarTo} = info.getValue();
    return (
      <FitRendererWithPopover
        value={overallFit}
        signalScore={signalScore}
        mostSimilarTo={mostSimilarTo}
      />
    );
  },
  header: 'Overall Fit',
  meta: {
    width: 97,
  },
});

const insightColumn = columnHelper.accessor((acc) => acc.insight, {
  id: 'insight',
  cell: (info) => {
    const value = info.getValue();
    if (!value) return null;
    if (value.length <= 80) {
      return <Text fontSize="sm">{value}</Text>;
    }
    return (
      <Popover isLazy placement="auto" trigger="hover">
        <PopoverTrigger>
          <Text noOfLines={3} fontSize="sm">
            {value}
          </Text>
        </PopoverTrigger>
        <PopoverContent p={2} maxW="400px">
          <PopoverArrow />
          <PopoverBody
            cursor="text"
            fontSize="sm"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    );
  },
  header: 'Insight',
  meta: {
    width: 400,
  },
});

const intentScoreColumn = columnHelper.accessor((acc) => acc, {
  id: 'intentScore',
  cell: (info) => {
    const account = info.getValue();
    return (
      <IntentTierRendererWithPopover
        tier={account.intent?.tier}
        score={account.intent?.score}
        enrichedFields={account.enrichedFields}
        fields={account.fields}
      />
    );
  },
  header: 'Intent Score',
  meta: {
    width: 110,
  },
});

const savedAtColumn = columnHelper.accessor(
  (acc) => acc.mutatedState.saveDate,
  {
    id: 'savedAt',
    cell: (info) => info.getValue()?.toLocaleDateString() ?? null,
    header: 'Saved On',
    meta: {
      width: 100,
    },
  }
);

const intentDateColumn = columnHelper.accessor((acc) => acc.intentDate, {
  id: 'intentDate',
  cell: (info) => info.getValue()?.toLocaleDateString() ?? null,
  header: 'Last Intent',
  meta: {
    width: 100,
  },
});

const categoriesColumn = columnHelper.accessor(
  (acc) => ({
    audience: acc.audience ?? [],
    primary: acc.primaryCategory ?? [],
    secondary: acc.secondaryCategory ?? [],
  }),
  {
    id: 'category',
    cell: (info) => (
      <CategoriesAndAudienceRenderer
        audiences={info.getValue().audience}
        primaryCategories={info.getValue().primary}
        secondaryCategories={info.getValue().secondary}
      />
    ),
    header: 'Categories',
    meta: {
      width: 250,
    },
  }
);

const tagsColumn = columnHelper.accessor(
  (acc) => ({
    staticListIds: acc.mutatedState.tags,
    accountId: acc.accountId,
  }),
  {
    id: 'tags',
    cell: (info) => (
      <ListsRenderer
        staticListIds={info.getValue().staticListIds}
        accountId={info.getValue().accountId}
      />
    ),
    header: 'Lists',
    meta: {
      width: 200,
    },
  }
);

const signalsColumn = columnHelper.accessor((acc) => acc.matchedSignals, {
  id: 'signals',
  cell: (info) => (
    <FitSignalsWithPopover signals={info.getValue()} maxCharacters={80} />
  ),
  header: 'Fit Signals',
  meta: {
    width: 400,
  },
});

const intentSignalsColumn = columnHelper.accessor(
  (acc) => ({
    enrichedFields: acc.enrichedFields,
    fields: acc.fields,
  }),
  {
    id: 'intentSignals',
    cell: (info) => {
      const {enrichedFields, fields} = info.getValue();
      return (
        <IntentSignalsWithPopover
          enrichedFields={enrichedFields}
          accountFields={fields}
          maxCharacters={80}
        />
      );
    },
    header: 'Intent Signals',
    meta: {
      width: 400,
    },
  }
);

const inCrmColumn = columnHelper.accessor((acc) => acc.inCrm, {
  id: 'inCRM',
  cell: (info) => (info.getValue() ? <Checkmark /> : null),
  header: 'CRM?',
  meta: {
    width: 55,
  },
});

const inSamColumn = columnHelper.accessor((acc) => acc.recommended, {
  id: 'inSAM',
  cell: (info) => (info.getValue() ? <Checkmark /> : null),
  header: 'SAM?',
  meta: {
    width: 55,
  },
});

const employeesColumn = columnHelper.accessor(
  (acc) => acc.fields.employeeCount,
  {
    id: 'employees',
    cell: (info) => info.getValue()?.toLocaleString(),
    header: 'Employees',
    meta: {
      width: 100,
    },
  }
);

const hqCountryColumn = columnHelper.accessor(
  (acc) =>
    CountryList[acc.fields.hqCountry as keyof typeof CountryList] ||
    acc.fields.hqCountry,
  {
    id: 'hq',
    header: 'HQ Country',
    meta: {
      width: 100,
    },
  }
);

const locationsColumn = columnHelper.accessor(
  (acc) =>
    (
      _.uniq(acc.fields.locationCountries).map(
        (code) => CountryList[code as keyof typeof CountryList]
      ) || _.uniq(acc.fields.locationCountries)
    ).join(', '),
  {
    id: 'locations',
    header: 'Locations',
    meta: {
      width: 150,
    },
  }
);

const taglineColumn = columnHelper.accessor((acc) => acc.fields.tagline, {
  id: 'tagline',
  header: 'Tagline',
  meta: {
    width: 280,
  },
});

const followersColumn = columnHelper.accessor(
  (acc) => acc.fields.followerCount,
  {
    id: 'followers',
    cell: (info) => info.getValue()?.toLocaleString(),
    header: 'Followers',
    meta: {
      width: 200,
    },
  }
);

const trafficRankColumn = columnHelper.accessor(
  (acc) => acc.fields.trafficRank,
  {
    id: 'trafficRank',
    cell: (info) => {
      // HACK: needed because trafficRank currently set to -1 if it's missing
      const value = info.getValue() || 0;
      return value > 0 ? value.toLocaleString() : '';
    },
    header: 'Traffic Rank',
    meta: {
      width: 140,
    },
  }
);

export const useColumnDefinitions = () => {
  const accountView = useAccountView();
  const intent = useIsFeatureEnabled('intent');
  const isFreemiumV3Customer = useIsFreemiumV3Customer();

  return useMemo(() => {
    if (isFreemiumV3Customer) {
      return [
        selectColumn,
        accountColumn(accountView),
        intentDateColumn,
        icpFitColumn,
        intentScoreColumn,
        insightColumn,
        intentSignalsColumn,
        categoriesColumn,
        employeesColumn,
        hqCountryColumn,
      ];
    }

    // Paid customers and legacy freemium customers
    return [
      selectColumn,
      accountColumn(accountView),
      tierColumn,
      overallFitColumn,
      ...(accountView !== 'recommended' && intent ? [intentScoreColumn] : []),
      ...(accountView === 'saved' ? [savedAtColumn] : []),
      ...(accountView === 'intent' ? [intentDateColumn] : []),
      categoriesColumn,
      ...(accountView !== 'recommended' ? [tagsColumn] : []),
      signalsColumn,
      ...(accountView !== 'recommended' && intent ? [intentSignalsColumn] : []),
      ...(accountView !== 'recommended' ? [inCrmColumn, inSamColumn] : []),
      employeesColumn,
      hqCountryColumn,
      locationsColumn,
      taglineColumn,
      followersColumn,
      trafficRankColumn,
    ];
  }, [accountView, isFreemiumV3Customer, intent]);
};
